import { message } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Cell, Legend, Pie, PieChart, Tooltip } from "recharts";
import { getCategories } from "../../../controller/services/common";
import i18n from "../../../localization/i18n";
import { setLoading } from "../../../redux/slices/common_slice";
import { convertCurrency, formatNumber } from "../../../utils/helper";

const COLORS = [
  "#FF0000", // Red
  "#ff4500c7", // OrangeRed
  "#FFA500", // Orange
  "#FFD700", // Gold
  "#FFFF00", // Yellow
  "#ADFF2F", // GreenYellow
  "#008000", // Green
  "#00FF00", // Lime
  "#00FA9A", // MediumSpringGreen
  "#00FFFF", // Cyan
  "#00BFFF", // DeepSkyBlue
  "#1E90FF", // DodgerBlue
  "#0000FF", // Blue
  "#8A2BE2", // BlueViolet
  "#DA70D6", // Orchid
  "#FF1493", // DeepPink
  "#FF69B4", // HotPink
  "#FFC0CB", // Pink
  "#FFB6C1", // LightPink
  "#FF7F50", // Coral
  "#FF6347", // Tomato
  "#FF8C00", // DarkOrange
  "#FF4500", // OrangeRed
  "#FF0000", // Red
  "#CD5C5C", // IndianRed
  "#F08080", // LightCoral
  "#DDA0DD", // Plum
  "#BA55D3", // MediumOrchid
  "#9370DB", // MediumPurple
  "#8B008B", // DarkMagenta
  "#800080", // Purple
  "#4B0082", // Indigo
  "#483D8B", // DarkSlateBlue
  "#4169E1", // RoyalBlue
  "#4682B4", // SteelBlue
  "#5F9EA0", // CadetBlue
  "#20B2AA", // LightSeaGreen
  "#3CB371", // MediumSeaGreen
  "#66CDAA", // MediumAquamarine
  "#7FFF00", // Chartreuse
  "#32CD32", // LimeGreen
  "#228B22", // ForestGreen
  "#008080", // Teal
  "#008B8B", // DarkCyan
  "#00CED1", // DarkTurquoise
  "#40E0D0", // Turquoise
  "#00BFFF", // DeepSkyBlue
  "#1E90FF", // DodgerBlue
  "#7B68EE", // MediumSlateBlue
  "#6A5ACD", // SlateBlue
  "#483D8B", // DarkSlateBlue
  "#C71585", // MediumVioletRed
  "#DB7093", // PaleVioletRed
  "#FF1493", // DeepPink
  "#FFC0CB", // Pink
  "#F5FFFA", // MintCream
  "#F0FFFF", // Azure
  "#E0FFFF", // LightCyan
  "#F0E68C", // Khaki
  "#FFD700", // Gold
  "#B8860B", // DarkGoldenRod
  "#A0522D", // Sienna
  "#8B4513", // SaddleBrown
];
export default function CommonPieChart({ type }) {
  const [dataArr, setDataArr] = useState([]);
  const currencySelected = useSelector(
    (state) => state.currency.currencySelected
  );
  const exchangeRates = useSelector((state) => state.currency.exchangeRates);
  const dashboardData = useSelector((state) => state.dashboard.dashboardData);
  const { t } = useTranslation();
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [savingCategories, setSavingCategories] = useState([]);
  const dispatch = useDispatch();
  const fields = [
    {
      name: "income",
      title: t("dashboard.incomeFields.1.title"),
      incomeType: t("dashboard.incomeFields.1.child"),
    },
    {
      name: "additionalIncome",
      title: t("dashboard.incomeFields.2.title"),
      type: "array",
    },
  ];

  const expenseFields = [
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "FoodCost",
      expenseType: t("dashboard.expenseFields.1.1"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "RentAmount",
      expenseType: t("dashboard.expenseFields.1.2"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      expenseType: t("dashboard.expenseFields.1.3"),
      name: "carInsurance",
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "carMaintenance",
      expenseType: t("dashboard.expenseFields.1.4"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "carPayment",
      expenseType: t("dashboard.expenseFields.1.5"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "clothesCost",
      expenseType: t("dashboard.expenseFields.1.6"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "condoFee",
      expenseType: t("dashboard.expenseFields.1.7"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "footwearCost",
      expenseType: t("dashboard.expenseFields.1.8"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "gasCost",
      expenseType: t("dashboard.expenseFields.1.9"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "giftCost",
      expenseType: t("dashboard.expenseFields.1.10"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "groceriesCost",
      expenseType: t("dashboard.expenseFields.1.11"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "homeMaintenance",
      expenseType: t("dashboard.expenseFields.1.12"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "internetCost",
      expenseType: t("dashboard.expenseFields.1.13"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "mobileBill",
      expenseType: t("dashboard.expenseFields.1.14"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "mortgage",
      expenseType: t("dashboard.expenseFields.1.15"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "propertyTax",
      expenseType: t("dashboard.expenseFields.1.16"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "tenantInsurance",
      expenseType: t("dashboard.expenseFields.1.17"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "sendHomeAmount",
      expenseType: t("dashboard.expenseFields.1.18"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "subscriptionBill",
      expenseType: t("dashboard.expenseFields.1.19"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "transportCost",
      expenseType: t("dashboard.expenseFields.1.20"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "utilitiesAmount",
      expenseType: t("dashboard.expenseFields.1.21"),
    },
    {
      title: t("dashboard.expenseFields.1.title"),
      name: "entertainmentCost",
      expenseType: t("dashboard.expenseFields.1.22"),
    },
    {
      title: t("dashboard.expenseFields.2"),
      name: "additionalExpense",
      type: "array",
    },
    {
      title: t("dashboard.expenseFields.3"),
      name: "debtDetails",
      type: "array",
    },
    {
      title: t("dashboard.expenseFields.4"),
      name: "currentSavingUp",
      type: "array",
    },
  ];

  useEffect(() => {
    if (type === "income" && incomeCategories?.length > 0) {
      getIncomeDataArr();
    } else if (
      expenseCategories?.length > 0 &&
      savingCategories?.length > 0 &&
      type !== "income"
    ) {
      
      getExpenseDataArr();
    }
  }, [
    dashboardData?.income,
    incomeCategories?.length,
    expenseCategories?.length,
    type,
    savingCategories?.length,
  ]);

  useEffect(() => {
    if (type === "income") {
      getCategoriesList("income");
    } else {
      getCategoriesList("expenses");
      getCategoriesList("savings");
    }
  }, [type, currencySelected]);
  const getCategoriesList = (categoryType) => {
    dispatch(setLoading(true));
    getCategories(categoryType)
      .then((resp) => {
        if (categoryType === "income") {
          setIncomeCategories(resp.data.category);
        }
        if (categoryType === "savings") {
          setSavingCategories(resp.data.category);
        }
        if (categoryType === "expenses") {
          const dataArr = resp.data.category
            ?.map((i) => [...i.subTitle])
            .flat(1);
          setExpenseCategories(dataArr);
        }
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const getExpenseDataArr = () => {
    const updatedArr = [];
    expenseFields.forEach((item) => {
      if (item.type === "array") {
        dashboardData?.expense?.[item?.name]?.forEach((innerItem) => {
          const foundExpenseType = expenseCategories.find(
            (i) => i.en === innerItem?.type
          );
          const foundSavingsType = savingCategories.find(
            (i) => i.title.en === innerItem?.type
          );
          if (innerItem?.amount || innerItem?.minPay) {
            const obj = {
              name:
                foundExpenseType?.[i18n?.language] ||
                foundSavingsType?.title?.[i18n.language] ||
                innerItem?.type ||
                innerItem?.title,
              amount: innerItem?.amount || innerItem?.minPay,
            };
            updatedArr?.push(obj);
          }
        });
      } else if (dashboardData?.expense?.[item?.name]) {
        
        const obj = {
          name: item.expenseType,
          amount: dashboardData?.expense?.[item?.name],
        };
        updatedArr.push(obj);
      }
    });
    setDataArr(updatedArr);
  };
  const getIncomeDataArr = () => {
    const updatedArr = [];
    fields.forEach((item) => {
      if (item.type === "array") {
        dashboardData?.income?.[item?.name]?.forEach((innerItem) => {
          const foundType = incomeCategories.find(
            (i) => i.title?.en?.toLowerCase() == innerItem?.type?.toLowerCase()
          );
          if (innerItem?.amount) {
            const obj = {
              name: foundType
                ? foundType?.title?.[i18n?.language]
                : innerItem?.type,
              amount: innerItem?.amount,
            };
            updatedArr?.push(obj);
          }
        });
      } else if (dashboardData?.income?.monthlyIncome) {
        const obj = {
          name: item.incomeType,
          amount: dashboardData?.income?.monthlyIncome,
        };
        updatedArr.push(obj);
      }
    });
    setDataArr(updatedArr);
  };
  return (
    <>
      <PieChart width={374} height={200}>
        <Tooltip content={CustomTooltip} />
        <Legend
          content={
            <RenderLegend
              dashboardData={dashboardData}
              currencySelected={currencySelected}
              type={type}
              exchangeRates={exchangeRates}
            />
          }
        />
        <Pie
          data={dataArr}
          cx={180}
          cy={90}
          innerRadius={60}
          outerRadius={95}
          fill="#8884d8"
          paddingAngle={0}
          dataKey="amount"
          // label
        >
          {dataArr.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
      </PieChart>
    </>
  );
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#f9f9f9",
          border: "1px solid #ddd",
          borderRadius: "4px",
          padding: "10px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        }}
      >
        {payload.map((entry, index) => (
          <p key={entry.value} style={{ margin: "0" }}>
            <span style={{ color: entry.color, fontWeight: "bold" }}>
              {entry.name}:
            </span>{" "}
            {formatNumber(entry.value)}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

const RenderLegend = (props) => {
  const { payload, dashboardData, type, exchangeRates, currencySelected } =
    props;
  return (
    <>
      <h5 className="m-2">
        {currencySelected}&nbsp;
        {formatNumber(
          convertCurrency(
            type === "income"
              ? dashboardData?.totalIncome
              : dashboardData?.totalExpense,
            exchangeRates,
            currencySelected
          ) || 0
        )}
      </h5>
      <ul className="list-inline mb-0 graph-item-details">
        {payload.map((entry, index) => (
          <li className="list-inline-item" key={entry.value}>
            <span
              style={{
                background: entry.color,
              }}
              className="graph-point"
            />{" "}
            {entry.value}
          </li>
        ))}
      </ul>
    </>
  );
};
